import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { FiDownload, FiBarChart2 } from 'react-icons/fi';

// --- Animations & Keyframes ---

const borderRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// --- Styled Components ---

const HeroContainer = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, var(--primary) 0%, #0B1D3A 100%);
  overflow: hidden;
  perspective: 1000px;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
                    linear-gradient(225deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
                    linear-gradient(315deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
                    linear-gradient(45deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%);
  background-size: 60px 60px;
  opacity: 0.4;
  z-index: 0;
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    padding-top: 100px;
    padding-bottom: 50px;
  }
`;

const HeroContent = styled.div`
  width: 50%;
  color: var(--white);
  
  @media (max-width: 992px) {
    width: 100%;
    order: 2;
    margin-top: var(--spacing-xl);
  }
`;

const SubHeading = styled(motion.div)`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  color: var(--accent);
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const BadgeLine = styled.span`
  display: inline-block;
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, var(--accent), transparent);
  margin-right: var(--spacing-md);
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const Heading = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: var(--spacing-lg);
  color: var(--white);
  
  @media (max-width: 992px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #fff 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const Description = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.7;
  margin-bottom: var(--spacing-xl);
  max-width: 550px;
  color: rgba(255, 255, 255, 0.85);
  
  @media (max-width: 992px) {
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ComingSection = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const ChipWrapper = styled(motion.div)`
  position: relative;
  display: inline-block;
`;

const AnimatedChip = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0.7rem 1.4rem;
  border-radius: 50px;
  background: rgba(10, 20, 40, 0.9);
  color: var(--accent);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;
  transition: transform 0.2s ease, box-shadow 0.3s ease;
  
  /* Inner content stays above the animated border */
  & > * {
    position: relative;
    z-index: 2;
  }
  
  /* Rotating gradient border */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: conic-gradient(
      from 0deg,
      transparent 0deg,
      var(--accent) 60deg,
      rgba(255, 255, 255, 0.8) 120deg,
      transparent 180deg,
      var(--accent) 240deg,
      rgba(255, 255, 255, 0.6) 300deg,
      transparent 360deg
    );
    border-radius: 50px;
    z-index: -2;
    animation: ${borderRotate} 12s linear infinite;
  }
  
  /* Inner background to mask the gradient */
  &::after {
    content: '';
    position: absolute;
    top: 1.5px;
    left: 1.5px;
    right: 1.5px;
    bottom: 1.5px;
    background: linear-gradient(135deg, rgba(10, 20, 40, 0.95) 0%, rgba(15, 30, 50, 0.95) 100%);
    border-radius: 50px;
    z-index: -1;
  }
  
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 0 20px rgba(71, 181, 255, 0.3);
    color: var(--accent);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ChipDot = styled.span`
  width: 8px;
  height: 8px;
  background-color: var(--accent);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--accent);
`;

const ButtonsWrapper = styled(motion.div)`
  display: flex;
  gap: var(--spacing-md);
  
  @media (max-width: 992px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }
`;

const PrimaryButton = styled(motion.a)`
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, var(--accent) 0%, #3a9bdc 100%);
  color: var(--white);
  border-radius: var(--border-radius-md);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px rgba(71, 181, 255, 0.25);
  position: relative;
  overflow: hidden;
  z-index: 1;
  text-decoration: none;
  
  svg {
    margin-left: var(--spacing-sm);
    transition: transform 0.2s;
  }
  
  &:hover svg {
    transform: translateY(2px);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(rgba(255,255,255,0.2), transparent);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover:after {
    opacity: 1;
  }
`;

const SecondaryButton = styled(motion.a)`
  padding: 1rem 2.5rem;
  background: transparent;
  color: var(--white);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: var(--border-radius-md);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.25s ease;
  
  svg {
    margin-left: var(--spacing-sm);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: var(--white);
    color: var(--white);
    transform: translateY(-2px);
  }
`;

const HeroVisual = styled(motion.div)`
  width: 45%;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1000px;
  
  @media (max-width: 992px) {
    width: 80%;
    order: 1;
    perspective: none; /* Disable 3D on mobile for better perf */
  }
`;

const PhoneImage = styled.img`
  max-height: 580px;
  width: auto;
  
  /* GPU acceleration & crisp rendering */
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  
  @media (max-width: 768px) {
    max-height: 450px;
  }
  
  @media (max-width: 480px) {
    max-height: 350px;
  }
`;

// --- Animation Variants ---

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// --- Main Component ---

const HeroSection = () => {
  // Motion values for global 3D parallax effect
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Hover state for phone depth effect
  const [isHovering, setIsHovering] = useState(false);

  // Smooth spring physics for global rotation (subtle)
  const rotateX = useSpring(useTransform(y, [0, window.innerHeight], [3, -3]), { stiffness: 100, damping: 30 });
  const rotateY = useSpring(useTransform(x, [0, window.innerWidth], [-3, 3]), { stiffness: 100, damping: 30 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, y]);

  return (
    <HeroContainer id="home">
      <BackgroundPattern />

      <ContentWrapper>
        <HeroContent>
          {/* initial={false}: text is visible on first paint (LCP), no fade-in from opacity 0 */}
          <motion.div
            variants={containerVariants}
            initial={false}
            animate="visible"
          >
            <SubHeading variants={itemVariants}>
              <BadgeLine /> XADevs LLC
            </SubHeading>

            <Heading variants={itemVariants}>
              AI Products &amp; <GradientText>Software Systems</GradientText>
            </Heading>

            <Description variants={itemVariants}>
              XADevs LLC designs and ships AI products and software systems: AI-native features, cloud backends, and cross-platform apps. Our products include VolpeTrack and HoldThen.
            </Description>

            <ComingSection variants={itemVariants}>
              <ChipWrapper
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <AnimatedChip href="/volpetrack">
                  <ChipDot />
                  <span>VolpeTrack is live on iOS and Android</span>
                </AnimatedChip>
              </ChipWrapper>
            </ComingSection>

            <ButtonsWrapper variants={itemVariants}>
              <PrimaryButton
                href="/#products"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Our Products <FiDownload />
              </PrimaryButton>
              <SecondaryButton
                href="/#about"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                About Us <FiBarChart2 />
              </SecondaryButton>
            </ButtonsWrapper>
          </motion.div>
        </HeroContent>

        <HeroVisual
          style={{ rotateX, rotateY }}
          initial={{ scale: 0.94, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 14, mass: 1 }}
        >
          {/* Outer layer: Hover effects (elevation, scale, shadow) */}
          <motion.div
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            animate={{
              y: isHovering ? -12 : 0,
              scale: isHovering ? 1.03 : 1,
              filter: isHovering
                ? 'drop-shadow(0 45px 70px rgba(0,0,0,0.5))'
                : 'drop-shadow(0 25px 50px rgba(0,0,0,0.35))',
            }}
            transition={{
              y: { type: 'spring', stiffness: 120, damping: 14 },
              scale: { type: 'spring', stiffness: 120, damping: 14 },
              filter: { duration: 0.5, ease: 'easeOut' },
            }}
            style={{
              cursor: 'pointer',
              willChange: 'transform',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
            }}
          >
            {/* Inner layer: Continuous floating animation */}
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotateZ: [0, 0.8, 0, -0.8, 0],
              }}
              transition={{
                y: { duration: 5, repeat: Infinity, ease: [0.45, 0, 0.55, 1] },
                rotateZ: { duration: 7, repeat: Infinity, ease: [0.45, 0, 0.55, 1] },
              }}
              style={{
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                willChange: 'transform',
                transform: 'translateZ(0)',
              }}
            >
              <PhoneImage
                src="/VolpeTrack-iPhone.webp"
                alt="VolpeTrack AI budget tracker app on iPhone"
                width="543"
                height="600"
                fetchPriority="high"
                decoding="async"
              />
            </motion.div>
          </motion.div>
        </HeroVisual>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroSection;
